import React, { useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Table, UncontrolledTooltip } from 'reactstrap';
import { useSelector } from 'react-redux';
import { GET_TEAM_INVITES } from 'graphql/queries';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { TEAM_SEND_INVITATION, TEAM_INVITATION_DELETE } from 'graphql/mutations';
import { graphqlLevelToAccessMap } from 'constants/global';
import useNotificationAlert from 'hooks/useNotificationAlert';
import useConfirmation from 'hooks/useConfirmation';
import { graphqlVisibilityToVisibilityMap } from 'constants/global';
import { useSortBy, useTable } from 'react-table';
import usePersistentSortState from 'hooks/usePersistentSortState';

const perPage = 100;

const sortingKeys = {
  name: 'name',
  email: 'email',
  level: 'level',
  visibility: 'visibility',
  createdAt: 'createdAt'
};

function ListInvites() {
  const { me } = useSelector((state) => state.auth);

  const { initialTableState, persistSortBy } = usePersistentSortState({
    storageKey: 'team_invites_table_state',
    sortingKeys
  });

  const {
    loading: isLoadingData,
    data,
    refetch,
    networkStatus
  } = useQuery(GET_TEAM_INVITES, {
    variables: { team_id: me?.team?.id, first: perPage, page: 1 },
    notifyOnNetworkStatusChange: true
  });

  const [teamSendInvitation, { loading: isLoadingResend }] = useMutation(TEAM_SEND_INVITATION);

  const [teamInvitationDelete, { loading: isLoadingDestroy }] = useMutation(TEAM_INVITATION_DELETE);

  const { show: showNotificationAlert } = useNotificationAlert({
    place: 'br'
  });

  const { showConfirmation, hideConfirmation } = useConfirmation(
    isLoadingResend || isLoadingDestroy
  );

  const onResend = ({ name, email, level, visibility }) => {
    showConfirmation({
      title: 'Are you sure want to resend?',
      confirmTitle: 'Resend',
      onConfirm: async () => {
        try {
          await teamSendInvitation({
            variables: {
              input: { name, email, level, visibility }
            }
          });

          showNotificationAlert({
            title: 'Success',
            message: 'You have resent the invitation successfully'
          });
        } catch (err) {
          console.log('__error', err);
        } finally {
          hideConfirmation();
        }
      }
    });
  };

  const onDeleteTeamInvitation = async (id) => {
    showConfirmation({
      title: 'Are you sure want to delete?',
      confirmTitle: 'Delete',
      onConfirm: async () => {
        try {
          await teamInvitationDelete({ variables: { invitationId: id } });
          hideConfirmation();
          showNotificationAlert({
            title: 'Success',
            message: 'You have deleted the team member successfully'
          });
          refetch();
        } catch (err) {
          console.log('__err', err.message);
        }
      }
    });
  };

  const teamInvites = useMemo(() => data?.getTeamInvites?.data || [], [data]);

  const columns = useMemo(() => {
    const sortIcon = (column) =>
      column.isSorted ? (
        column.isSortedDesc ? (
          <span className="sorted-icon sorted-icon__desc" />
        ) : (
          <span className="sorted-icon sorted-icon__asc" />
        )
      ) : (
        <span className="sorted-icon" />
      );

    return [
      {
        Header: ({ column }) => <span className="heading-w-icon">Name {sortIcon(column)}</span>,
        accessor: 'name'
      },
      {
        Header: ({ column }) => <span className="heading-w-icon">Email {sortIcon(column)}</span>,
        accessor: 'email'
      },
      {
        Header: ({ column }) => <span className="heading-w-icon">Access {sortIcon(column)}</span>,
        accessor: 'level',
        Cell: ({ value }) => graphqlLevelToAccessMap[value]
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Visibility {sortIcon(column)}</span>
        ),
        accessor: 'visibility',
        Cell: ({ value }) => graphqlVisibilityToVisibilityMap[value]
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Date Sent {sortIcon(column)}</span>
        ),
        accessor: 'createdAt'
      },
      {
        Header: () => '',
        id: 'action',
        disableSortBy: true,
        Cell: ({ row }) => {
          const invite = row.original;

          return (
            <div className="table-actions">
              <div className="d-flex">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onResend(invite);
                  }}
                  className="table-action"
                  id={`tooltip-resend-${invite.id}`}>
                  Resend
                </button>
                <UncontrolledTooltip delay={0} target={`tooltip-resend-${invite.id}`}>
                  Resend Invitation
                </UncontrolledTooltip>
                {me?.level === 'ADMIN' && (
                  <>
                    <button
                      className="table-action table-action-delete"
                      id={`tooltip-delete-${invite.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onDeleteTeamInvitation(invite.id);
                      }}>
                      <i className="fas fa-trash" />
                    </button>
                    <UncontrolledTooltip delay={0} target={`tooltip-delete-${invite.id}`}>
                      Delete Team
                    </UncontrolledTooltip>
                  </>
                )}
              </div>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: tableState
  } = useTable(
    {
      columns,
      data: teamInvites,
      disableSortRemove: true,
      initialState: initialTableState
    },
    useSortBy
  );

  useEffect(() => {
    persistSortBy(tableState.sortBy);
  }, [persistSortBy, tableState.sortBy]);

  return (
    <Row>
      <div className="col">
        <Card>
          <CardHeader className="border-0">
            <Row>
              <Col>
                <h3 className="mb-0 text-brand">List Invited Company Members</h3>
              </Col>
            </Row>
          </CardHeader>

          {(isLoadingData || networkStatus === NetworkStatus.refetch) && (
            <CardBody>
              <p className="text-center">Loading...</p>
            </CardBody>
          )}
          {!isLoadingData && data && data.getTeamInvites?.data.length <= 0 && (
            <CardBody>
              <p className="text-center">There is no data!</p>
            </CardBody>
          )}
          {!isLoadingData && data && data.getTeamInvites?.data.length >= 1 && (
            <Table
              className="align-items-center table-flush"
              responsive
              striped
              {...getTableProps()}>
              <thead className="thead-light">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </Row>
  );
}

export default ListInvites;
